import { Helmet } from "react-helmet-async";

const DEFAULTS = {
  siteName: "One Step Ahead Parents",
  siteUrl: "https://onestepaheadparents.com",
  twitter: "@onestepaheadparents",
};

/**
 * Auto-derive editorial OG image key from route path.
 * Maps must match keys defined in backend/og_service.py PAGES.
 */
function ogKeyFromPath(path) {
  const p = (path || "/").split("?")[0].split("#")[0];
  if (p === "/" || p === "") return "home";
  if (p === "/about") return "about";
  if (p === "/contact") return "contact";
  if (p === "/resources") return "resources";
  if (p === "/products") return "products";
  if (p.startsWith("/blog")) return "blog";
  if (p.startsWith("/products/")) {
    const slug = p.replace("/products/", "").replace(/\/$/, "");
    return slug || "products";
  }
  return "default";
}

/**
 * Per-page SEO. Pass title, description, path, and optionally image, type, noindex.
 *   <Seo title="Resources" description="..." path="/resources" />
 * Auto-generates a magazine-cover editorial OG image (1200x630) via /api/og/<key>.png
 * unless an explicit `image` prop is provided.
 */
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
  keywords,
  publishedAt,
  category,
}) {
  const fullTitle = title
    ? `${title} · ${DEFAULTS.siteName}`
    : `${DEFAULTS.siteName}; Stay one step ahead of elementary school chaos`;
  const url = `${DEFAULTS.siteUrl}${path}`;
  const ogImage = image || `${DEFAULTS.siteUrl}/api/og/${ogKeyFromPath(path)}.png`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULTS.siteName} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {category && <meta property="article:section" content={category} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={DEFAULTS.twitter} />
      <meta name="twitter:creator" content={DEFAULTS.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
